import { makeAutoObservable, runInAction } from "mobx";
import {
  Answer,
  Attempt,
  AttemptSummary,
  ExamSummary,
  User,
  LearningStatistics,
  RandomTag,
  BankTask,
} from "./learning_model";
import { HttpError, LearningHttpRepository } from "./learning_http_repository";

export function parseDraft(attempt: Attempt, drafts: Answer[]): Answer[] {
  return drafts.map((value, index) => {
    if (attempt.tasks[index].type !== "onlyNumber" || typeof value !== "string")
      return value;
    if (!value.trim()) return null;
    if (
      !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim()) ||
      !Number.isFinite(Number(value))
    ) {
      throw new Error(`Введите конечное число в задании ${index + 1}`);
    }
    return Number(value);
  });
}
export class LearningStore {
  repository = new LearningHttpRepository();
  user?: User;
  initialized = false;
  busy = false;
  error = "";
  conflict = false;
  notice = "";
  exams: ExamSummary[] = [];
  attempts: AttemptSummary[] = [];
  statistics?: LearningStatistics;
  statisticsError = "";
  randomTags: RandomTag[] = [];
  randomTagsError = "";
  variants: ExamSummary[] = [];
  variantPage = 0;
  bankTasks: BankTask[] = [];
  bankTopics: RandomTag[] = [];
  bankPage = 0;
  bankHasNext = false;
  bankTag = "";
  bankQuery = "";
  bankBusy = false;
  bankError = "";
  bankRequest = 0;
  examPage = 0;
  historyPage = 0;
  attempt?: Attempt;
  drafts: Answer[] = [];
  baseline = "[]";
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
  get dirty() {
    return !!this.attempt && JSON.stringify(this.drafts) !== this.baseline;
  }
  isExpired() {
    return (
      !!this.attempt &&
      (this.attempt.expired === true ||
        (!!this.attempt.expiresAt &&
          Date.parse(this.attempt.expiresAt) <= Date.now()))
    );
  }
  setAnswer(index: number, value: Answer) {
    if (
      !this.attempt ||
      this.attempt.status !== "in_progress" ||
      this.isExpired() ||
      this.busy
    )
      return;
    this.drafts[index] = value;
    this.notice = "";
  }
  accept(attempt: Attempt) {
    this.attempt = attempt;
    this.drafts = attempt.answers.map((value) =>
      Array.isArray(value) ? [...value] : value,
    );
    this.baseline = JSON.stringify(this.drafts);
    this.conflict = false;
  }
  fail(error: unknown) {
    this.error =
      error instanceof Error ? error.message : "Не удалось выполнить операцию";
    if (error instanceof HttpError && error.status === 410 && this.attempt)
      this.attempt.expired = true;
    this.conflict = error instanceof HttpError && error.status === 409;
    if (error instanceof HttpError && error.status === 401) {
      this.user = undefined;
      this.attempt = undefined;
      this.drafts = [];
      this.baseline = "[]";
    }
  }
  async init() {
    if (this.busy) return;
    this.busy = true;
    try {
      const result = await this.repository.me();
      runInAction(() => {
        this.user = result.user;
      });
    } catch (error) {
      if (!(error instanceof HttpError && error.status === 401))
        runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
        this.initialized = true;
      });
    }
  }
  async login(username: string, password: string) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const user = await this.repository.login(username, password);
      runInAction(() => {
        this.user = user;
      });
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async logout() {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      await this.repository.logout();
      runInAction(() => {
        this.user = undefined;
        this.attempt = undefined;
        this.drafts = [];
        this.baseline = "[]";
        this.exams = [];
        this.attempts = [];
        this.statistics = undefined;
        this.randomTags = [];
        this.variants = [];
        this.bankTasks = [];
        this.bankTopics = [];
        this.bankError = "";
        this.bankRequest++;
        this.statisticsError = "";
        this.randomTagsError = "";
      });
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async lobby(examPage = this.examPage, historyPage = this.historyPage) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    this.notice = "";
    try {
      const [exams, attempts, variants] = await Promise.all([
        this.repository.exams(examPage),
        this.repository.attempts(historyPage),
        this.repository.exams(this.variantPage, "variant"),
        this.refreshStatistics(),
        this.refreshRandomTags(),
      ]);
      runInAction(() => {
        this.exams = exams;
        this.attempts = attempts;
        this.variants = variants;
        this.examPage = examPage;
        this.historyPage = historyPage;
        this.attempt = undefined;
        this.drafts = [];
        this.baseline = "[]";
      });
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async refreshStatistics() {
    try {
      const statistics = await this.repository.statistics();
      runInAction(() => {
        this.statistics = statistics;
        this.statisticsError = "";
      });
    } catch (error) {
      runInAction(() => {
        this.statistics = undefined;
        this.statisticsError = "Не удалось загрузить статистику.";
        if (error instanceof HttpError && error.status === 401)
          this.fail(error);
      });
    }
  }
  async refreshRandomTags() {
    try {
      const tags = await this.repository.randomTags();
      runInAction(() => {
        this.randomTags = tags;
        this.randomTagsError = "";
      });
    } catch (error) {
      runInAction(() => {
        this.randomTags = [];
        this.randomTagsError =
          "Не удалось загрузить теги для случайного теста.";
        if (error instanceof HttpError && error.status === 401)
          this.fail(error);
      });
    }
  }
  async listVariants(page = this.variantPage) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const variants = await this.repository.exams(page, "variant");
      runInAction(() => {
        this.variants = variants;
        this.variantPage = page;
      });
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async searchBank(page = 0, tag = this.bankTag, query = this.bankQuery) {
    const request = ++this.bankRequest;
    this.bankBusy = true;
    this.bankError = "";
    try {
      const [result, topics] = await Promise.all([
        this.repository.bank(page, tag, query),
        this.repository.bankTopics(),
      ]);
      runInAction(() => {
        if (request !== this.bankRequest) return;
        this.bankTasks = result.tasks;
        this.bankHasNext = result.hasNext;
        this.bankTopics = topics;
        this.bankPage = page;
        this.bankTag = tag;
        this.bankQuery = query;
      });
    } catch (error) {
      runInAction(() => {
        if (request === this.bankRequest) {
          this.bankError =
            error instanceof Error
              ? error.message
              : "Не удалось загрузить банк";
          if (error instanceof HttpError && error.status === 401)
            this.fail(error);
        }
      });
    } finally {
      runInAction(() => {
        if (request === this.bankRequest) this.bankBusy = false;
      });
    }
  }
  async startBankTask(id: string) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const attempt = await this.repository.startBankTask(id);
      runInAction(() => this.accept(attempt));
      return attempt.id;
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async construct(
    title: string,
    selections: { tag: string; count: number }[],
    totalScore: number,
  ) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const attempt = await this.repository.construct(
        title,
        selections,
        totalScore,
      );
      runInAction(() => this.accept(attempt));
      return attempt.id;
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async startRandom(count: number, tags: string[], totalScore: number) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const attempt = await this.repository.startRandom(
        count,
        tags,
        totalScore,
      );
      runInAction(() => this.accept(attempt));
      return attempt.id;
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async load(id: string) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    this.notice = "";
    this.attempt = undefined;
    try {
      const attempt = await this.repository.attempt(id);
      runInAction(() => this.accept(attempt));
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async start(id: string) {
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    try {
      const attempt = await this.repository.start(id);
      runInAction(() => this.accept(attempt));
      return attempt.id;
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
  async save(finish = false) {
    if (
      this.busy ||
      !this.attempt ||
      this.attempt.status !== "in_progress" ||
      this.isExpired()
    )
      return;
    this.busy = true;
    this.error = "";
    this.notice = "";
    this.conflict = false;
    try {
      let attempt = this.attempt;
      if (this.dirty) {
        attempt = await this.repository.save(
          attempt.id,
          attempt.revision,
          parseDraft(attempt, this.drafts),
        );
        runInAction(() => this.accept(attempt));
      }
      if (finish) {
        attempt = await this.repository.finish(attempt.id, attempt.revision);
        runInAction(() => this.accept(attempt));
      }
      runInAction(() => {
        this.notice = finish ? "Попытка завершена." : "Ответы сохранены.";
      });
    } catch (error) {
      runInAction(() => this.fail(error));
    } finally {
      runInAction(() => {
        this.busy = false;
      });
    }
  }
}
